<template>
  <div id="app">
    <Header :logo="logo"/>
    <Hero :headline="headline" :lead="lead" :image="image"/>
    <Subnav :subnav="subnav"/>
    <div class="diag-grad">
      <Sections v-for="post in sections" v-bind:post="post"/>
      <Footer/>
    </div>
  </div>
</template>

<script>
import Header from './components/Header.vue'
import Hero from './components/Hero.vue'
import Subnav from './components/Subnav.vue'
import Sections from './components/Section.vue'
import Footer from './components/Footer.vue'

export default {
  name: 'app',
  components: {
    Header,
    Hero,
    Subnav,
    Sections,
    Footer
  },
  data: function () {
    return {
      logo: 'logo.svg',
      headline: '<span>Our&nbsp;</span><span>Commitment&nbsp;</span><span>to&nbsp;</span><span>Safety</span>',
      lead: 'Medical technology innovators are pioneering new devices and technologies for one reason alone—to improve and save the lives of patients. To that end, patient safety is at the heart of what we do. The medical technology industry is committed to safety, effectiveness and quality for all devices. Medical technologies are thoroughly and rigorously tested by companies before they are submitted to FDA for exhaustive, science-based reviews.',
      image: 'safety_and_quality_hero.jpg',
      subnav: [
        {
          anchor: 'section-1',
          title: 'Patients Matter',
          image: 'image-1.png'
        },
        {
          anchor: 'section-2',
          title: 'Safety Matters',
          image: 'image-2.png'
        },
        {
          anchor: 'section-3',
          title: 'Regulation Matters',
          image: 'image-3.png'
        },
        {
          anchor: 'section-4',
          title: 'Innovation Matters',
          image: 'image-4.png'
        }
      ],
      sections: [
        {
          id: 1,
          title: 'Patients Matter',
          stat: '49 million+ people living with implantable devices',
          highlight: '49',
          description: '<p>Patient safety has always been and always will be our #1 priority. Over 49 million people live with implantable devices such as intraocular lens implants (IOLs), implantable cardioverter defibrillators (ICDs), pacemakers and artificial knees and hips. Patients turn to medtech to give them independence and opportunities, whether that means the ability to play with grandchildren or to return to work quickly. The medical technology industry puts patients’ needs first and creates innovative devices to improve lives around the world.</p>',
          hidden: '<p>Millions of people depend on implanted medical devices to improve their quality of life, leading to a happier and more productive society. These devices range from pacemakers that helps keep hearts beating, to intraocular lenses that restore sight, to deep brain stimulators that relieve tremors in neurological patients.</p>',
          graphic: false,
          small: 1,
          large: 2,
          offset: 0,
          imageOffset: 1
        },
        {
          id: 2,
          title: 'Safety Matters',
          stat: '9 checkpoints to ensure patient safety',
          highlight: '9',
          description: '<p>Throughout the lifecycle of a medical device, it is carefully tested, reviewed and modified to minimize risk. Rigorous testing, extensive FDA reviews and post-market reporting ensure medical devices are safe and effective for millions of patients.</p>',
          hidden: '<img class="desktop-img" src="./assets/nine-steps.svg"/><img class="mobile-img" src="./assets/nine-steps-mobile.svg"/>',
          graphic: true,
          small: 2,
          large: 1,
          offset: 2,
          imageOffset: 0
        },
        {
          id: 3,
          title: 'Regulation Matters',
          stat: '42 years of evolving safety standards',
          highlight: '42',
          description: '<p>Scientists. Physicians. Engineers. Quality Experts. Countless professionals thoroughly research and examine every aspect of a medical device. Every year the FDA subjects thousands of medical devices to intensive, scientific reviews based on extensive data to ensure that they meet their pre-determined specifications of safety and effectiveness. Industry and regulatory bodies work together to identify problems, create solutions and drive shared progress toward the shared goal of getting patients safe, effective products when they need them.</p>',
          hidden: '<p>The premarket notification, or 510(k) process, and the premarket approval (PMA) application process are the regulatory pathways that ensure safety, effectiveness and quality in every medical device.</p><p>Low-to moderate-risk device types that have a well-established safety profile are cleared through the 510(k) process. Examples of such devices include bandages or wheelchairs. The 510(k) process is well designed to assess the safety and effectiveness of such devices and has an incredible record of safety.</p><p>Higher-risk technologies that often involve novel or first-of-a-kind innovations follow the PMA process. The PMA process is the FDA’s most rigorous premarket device submission and includes an in-depth scientific and regulatory review. These higher-risk devices, like heart valves or spinal stimulators, are critical to the lives of patients.</p>',
          graphic: false,
          small: 1,
          large: 2,
          offset: 0,
          imageOffset: 1
        },
        {
          id: 4,
          title: 'Innovation Matters',
          stat: 'Medical technology has added 5 years to U.S. life expectancy',
          highlight: '5',
          description: '<p>Thanks to lifesaving medical technology patients are living longer, happier, healthier and more productive lives. Five years added to U.S. life expectancy means more time with family, more birthdays and more life moments.</p>',
          hidden: '<p>The scale and scope of medical technology extends beyond the macro level of health care. Medical technology is directly tackling America’s most pressing health care challenges through rapid innovation: </p><ul><li>Disability rates have declined by 25% and disability-free life expectancy has increased</li><li>The number of days a person spends in the hospital on average has declined by 59%</li><li>Reduced fatalities from heart disease and stroke by more than half over the past 30 years</li></ul>',
          graphic: false,
          small: 2,
          large: 1,
          offset: 2,
          imageOffset: 0
        },
      ]
    }
  }
}
</script>

<style lang="scss">
body {
  @include sans-font(normal);
  font-size: 23px;
  line-height: 1.3;
}
h1 {
  @include serif-font();
  font-size: 123px;
}
.diag-grad {
  background-image:
    repeating-linear-gradient(
      45deg,
      $white,
      $white 25%,
      $white2 25.01%,
      $white2 50% /* determines size */
    );
}
.row {
  width: 100%;
}
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
